import { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch } from '../util/authFetch';
import { handleUnauthorizedRedirect } from '../util/authRedirect';

// ============================================================================
// Types
// ============================================================================

export interface UseFetchOptions {
  enabled?: boolean;
  initialData?: unknown;
  parseJson?: boolean;
  skipAuth?: boolean;
}

export interface UseFetchResult<T, TBody = unknown> {
  execute: (body?: TBody) => Promise<T | null>;
  data: T | null;
  error: string | null;
  loading: boolean;
  status: number | null;
}

// ============================================================================
// Base Hook
// ============================================================================

function useBaseFetch<T, TBody = unknown>(
  url: string | null,
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  options: UseFetchOptions = {}
): UseFetchResult<T, TBody> {
  const { enabled = true, initialData = null, parseJson, skipAuth = false } = options;
  const [data, setData] = useState<T | null>(initialData as T | null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const execute = useCallback(async (body?: TBody): Promise<T | null> => {
    if (!url) return null;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const headers: HeadersInit = {};

      // Add Content-Type for methods with JSON body
      if (body !== undefined && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
        headers['Content-Type'] = 'application/json';
      }

      const fetchOptions: RequestInit = {
        method,
        headers,
        signal: controller.signal,
      };

      // Add body for methods with payload
      if (body !== undefined) {
        fetchOptions.body = JSON.stringify(body);
      }

      const response = skipAuth
        ? await fetch(url, fetchOptions)
        : await authFetch(url, fetchOptions);
      setStatus(response.status);

      const rawText = await response.text();
      const contentType = response.headers.get('content-type') || '';
      const shouldParseJson = parseJson ?? contentType.includes('application/json');
      
      let payload: unknown = rawText;
      if (shouldParseJson && rawText) {
        try {
          payload = JSON.parse(rawText);
        } catch {
          payload = rawText;
        }
      }

      if (!response.ok) {
        if (!skipAuth && response.status === 401) {
          handleUnauthorizedRedirect();
        }
        const objectMessage =
          payload && typeof payload === 'object'
            ? (
                (payload as { message?: string; error?: string; errors?: string[] }).message ||
                (payload as { message?: string; error?: string; errors?: string[] }).error ||
                (payload as { message?: string; error?: string; errors?: string[] }).errors?.[0]
              )
            : undefined;
        const message =
          typeof payload === 'string'
            ? payload
            : objectMessage || response.statusText || `Request failed: ${response.status}`;
        setError(message || `Request failed: ${response.status}`);
        return null;
      }

      setData(payload as T);
      return payload as T;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return null;
      }
      const errorMessage = err instanceof Error ? err.message : 'Request failed';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [url, method, skipAuth, parseJson]);

  // Clear stale data and abort in-flight requests when URL changes
  const prevUrlRef = useRef(url);
  useEffect(() => {
    if (prevUrlRef.current !== url) {
      prevUrlRef.current = url;
      setData(initialData as T | null);
      setError(null);
      setStatus(null);
    }
    return () => {
      abortRef.current?.abort();
    };
  }, [enabled, url, initialData]);

  return { execute, data, error, loading, status };
}

// ============================================================================
// HTTP Method Hooks
// ============================================================================

/**
 * GET request hook - auto-executes on mount when enabled
 */
export function useGet<T>(
  url: string | null,
  options: UseFetchOptions = {}
): UseFetchResult<T, void> {
  const result = useBaseFetch<T, void>(url, 'GET', options);
  const { enabled = true } = options;

  useEffect(() => {
    if (enabled && url) {
      result.execute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, url]);

  return result;
}

/**
 * POST request hook - manual execute with body
 */
export function usePost<T, TBody = unknown>(
  url: string | null,
  options: UseFetchOptions = {}
): UseFetchResult<T, TBody> {
  return useBaseFetch<T, TBody>(url, 'POST', options);
}

/**
 * PATCH request hook - manual execute with body
 */
export function usePatch<T, TBody = unknown>(
  url: string | null,
  options: UseFetchOptions = {}
): UseFetchResult<T, TBody> {
  return useBaseFetch<T, TBody>(url, 'PATCH', options);
}

/**
 * PUT request hook - manual execute with body
 */
export function usePut<T, TBody = unknown>(
  url: string | null,
  options: UseFetchOptions = {}
): UseFetchResult<T, TBody> {
  return useBaseFetch<T, TBody>(url, 'PUT', options);
}

/**
 * DELETE request hook - manual execute
 */
export function useDelete<T>(
  url: string | null,
  options: UseFetchOptions = {}
): UseFetchResult<T, void> {
  return useBaseFetch<T, void>(url, 'DELETE', options);
}

// ============================================================================
// Legacy useFetch (backwards compatibility)
// ============================================================================

/**
 * @deprecated Use useGet, usePost, usePut, useDelete instead
 */
export function useFetch<T = unknown>(
  url: string | null,
  options: UseFetchOptions & { method?: string } = {}
): UseFetchResult<T, unknown> {
  const { method = 'GET', ...rest } = options;
  const httpMethod = (method.toUpperCase() as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') || 'GET';
  
  return useBaseFetch<T, unknown>(url, httpMethod, rest);
}
